/**
 * Schema transform functions and utility for resolving dot-notation source paths.
 * Used by useDynamicSavePayload and DynamicCredentialsTab to build payloads from schema definitions.
 */

/**
 * Resolves a dot-notation path against multiple source objects.
 * Paths are resolved in order: dynamicState, props, secretResults.
 * Explicit prefixes: "fields." resolves from props.fields, "secretResults." from secretResults.
 *
 * @param {string} path - Dot-notation path (e.g. "bugsDateBack", "fields.snAffectedCIQuery", "secretResults.supportSecretId.Name")
 * @param {Object} props - Component props from ManagedProductConfig
 * @param {Object} dynamicState - Dynamic state from DynamicConfigForm
 * @param {Object} [secretResults={}] - Results from credential secret saves
 * @returns {*} Resolved value or undefined
 */
export function resolveSource(path, props, dynamicState, secretResults = {}) {
  if (!path || typeof path !== 'string') return undefined;

  const parts = path.split('.');

  // Explicit prefix routing
  if (parts[0] === 'fields') {
    return getNestedValue(props.fields, parts.slice(1));
  }
  if (parts[0] === 'secretResults') {
    return getNestedValue(secretResults, parts.slice(1));
  }

  // Check dynamicState first, then props
  const dynamicVal = getNestedValue(dynamicState, parts);
  if (dynamicVal !== undefined) return dynamicVal;

  return getNestedValue(props, parts);
}

/**
 * Safely traverse an object by an array of keys.
 * @param {Object} obj
 * @param {string[]} keys
 * @returns {*}
 */
function getNestedValue(obj, keys) {
  if (!obj) return undefined;
  let current = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[key];
  }
  return current;
}

/**
 * Named transform functions referenced by schema transform strings.
 */
export const transforms = {
  /**
   * Converts priorityMaps array to the format expected by saveSetting API.
   */
  priorityMapTransform: priorityMaps => {
    if (!Array.isArray(priorityMaps)) return [];
    return priorityMaps.map(mapping => ({
      value: mapping.vendorPriority?.value,
      label: mapping.vendorPriority?.value,
      snPriority: mapping.snPriority?.value
    }));
  },

  /**
   * Extracts snCompanySysId value, falling back to raw field value.
   */
  snCompanyTransform: (snCompanySysId, props) => {
    if (snCompanySysId?.value) return snCompanySysId.value;
    return props?.fields?.snCompanySysId || snCompanySysId;
  },

  /**
   * Converts boolean to integer (0 or 1).
   */
  boolToInt: val => (val === false ? 0 : 1),

  /**
   * Converts priorityMaps array preserving the isCustom flag (used by MSFTSqlServer).
   */
  priorityMapTransformWithCustom: priorityMaps => {
    if (!Array.isArray(priorityMaps)) return [];
    return priorityMaps.map(mapping => ({
      value: mapping.vendorPriority?.value,
      label: mapping.vendorPriority?.value,
      isCustom: mapping.vendorPriority?.isCustom,
      snPriority: mapping.snPriority?.value
    }));
  },

  /**
   * Identity transform - returns value as-is.
   */
  identity: val => val,

  /**
   * Strips {label, value} wrappers from a multi-select array so the persisted
   * payload is plain strings. Backend handlers (e.g. cloud_inventory_sync,
   * managed_products/base) expect strings; the FE multi-select holds dicts.
   */
  unwrapValueList: items => {
    if (!Array.isArray(items)) return items;
    return items.map(i => (i && typeof i === 'object' && 'value' in i ? i.value : i));
  }
};

/**
 * Applies a named transform to a value.
 * @param {string} transformName - Key in the transforms object
 * @param {*} value - Value to transform
 * @param {Object} props - Props for transforms that need additional context
 * @returns {*} Transformed value
 */
export function applyTransform(transformName, value, props) {
  const fn = transforms[transformName];
  if (!fn) {
    console.warn(`Unknown transform: ${transformName}`);
    return value;
  }
  return fn(value, props);
}

/**
 * State keys backed by dedicated useState hooks in DynamicConfigForm (restored
 * separately on load and flowed into the save via allProps).
 *
 * They must NOT also be mirrored into dynamicState: resolveSource() reads
 * dynamicState BEFORE props, but the ToggleWidget only updates the useState — so
 * a stale dynamicState copy loaded from settings would win at save time. That's
 * the "enable works but disable doesn't persist" bug (Red Hat "Enable Version
 * Stream Bugs", GH #436): after a saved-enabled state, the loaded `true` sat in
 * dynamicState and got re-saved no matter how the switch was toggled.
 */
export const RESERVED_DEDICATED_STATE_KEYS = new Set([
  'enableProductFamilyBugs',
  'enableInferredVersions',
  'enableVersionStreamBugs',
  'autoClose'
]);

/**
 * Build the initial dynamicState from saved settings for a schema's
 * postCredentialWidgets + defaultConfigExtras, EXCLUDING keys owned by dedicated
 * useState hooks (see RESERVED_DEDICATED_STATE_KEYS) so those stay single-source.
 *
 * @param {Array} widgets - formSchema.credentials.postCredentialWidgets
 * @param {Array} extras - formSchema.defaultConfigExtras
 * @param {Object} settingsValues - the saved settings.value object
 * @returns {Object} initial dynamicState (reserved keys omitted)
 */
export function buildInitialDynamicState(widgets, extras, settingsValues) {
  const initialDynamic = {};
  [...(widgets || []), ...(extras || [])].forEach(widget => {
    if (!widget.stateKey) return;
    if (RESERVED_DEDICATED_STATE_KEYS.has(widget.stateKey)) return;
    const loadKey = widget.settingsKey || widget.stateKey;
    if (settingsValues && settingsValues[loadKey] !== undefined) {
      initialDynamic[widget.stateKey] = settingsValues[loadKey];
    }
  });
  return initialDynamic;
}
